/**
 * The plot's own ground colour, read off the pyramid it stands on: the
 * orthoimage's raw mean and low-pass boxdown a build shades by, and the
 * colour a leaf's albedo hands the plot as a deeper tile comes in.
 *
 * Reads the closure it was cut out of through `GroundColourDeps`. `plot` and
 * `plotBuilding` stay behind `deps.` rather than being destructured: each is
 * a `let` this module only reads, and a destructure would snapshot a stale
 * value the moment the outer closure reassigns it. `plotGround` and
 * `needsRender` stay behind `deps.` too, since both are written here as well
 * as read.
 */

import * as THREE from 'three';

import {
  directionForLatLon, faceUvForDirection, formatTileKey, tileForLatLon, tilesAcross,
} from './tileTree.ts';
import type { TileAlbedoRef } from './tileTree.ts';
import { tileAlbedoUv } from './tileAlbedoUv.ts';
import { LOWPASS_M } from './siteAlbedo.ts';
import type { PyramidViewHandle } from './pyramidView';
import type { DtmPlot } from './dtmPlot';
import type { AtlasLive } from '../atlas';
import type { InjectsHandle } from './injects.ts';
import type { PlotBuildHandle } from './plotBuild.ts';
import type { PlotGeometryHandle } from './plotGeometry';
import { readableCanvas2d } from './canvas2d.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';
import { albedoByteTable, type AlbedoTransfer } from './albedoTransfer.ts';

declare global {
  interface Window {
    /** DEV only: `followPlotGroundColour`'s owed-retry count, read by
     *  `plot-colour-check.mjs`. A build never defines it. */
    __plotGroundReadbacks?: number;
  }
}

export type PlotGround = {
  plot: DtmPlot; latRad: number; lonRad: number;
  /** The level the plot's colour came from, -1 before it has one. */
  level: number;
  /** The deepest leaf there is drawn but its colour was not readable yet. */
  owed: boolean;
  /** Wall time an owed read has waited since it last retried, seconds. */
  owedWaitS: number;
  ortho: THREE.Texture | null; orthoMean: number | null; hasLowPass: boolean;
  widthM: number; heightM: number;
} | null;

export interface GroundColourDeps {
  readonly injects: InjectsHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly live: AtlasLive;
  readonly pyramidView: PyramidViewHandle;
  readonly plotGeometry: Pick<PlotGeometryHandle, 'latLonAt'>;
  /** How the tile body's albedo bytes decode; null is sRGB colour. */
  readonly albedoTransferFor: (bodyId: string | null) => AlbedoTransfer | null;
}

export interface GroundColourHandle {
  orthoMeanOf(
    tex: THREE.Texture, band: ((name: string) => Promise<void>) | null,
  ): Promise<number | null>;
  orthoLowPassOf(tex: THREE.Texture, widthM: number, heightM: number): THREE.Texture | null;
  inheritedGroundColour(
    latRad: number, lonRad: number, deeperThan?: number,
  ): { rgb: [number, number, number] | null; level: number } | null;
  inheritedGroundColourMap(
    widthM: number, heightM: number, fallback: readonly [number, number, number],
    band: ((name: string) => Promise<void>) | null,
  ): Promise<THREE.DataTexture | null>;
  followPlotGroundColour(dt: number): void;
  srgbToLinear(byte: number): number;
}

export function createGroundColour(deps: GroundColourDeps): GroundColourHandle {
  const { pyramidView } = deps;
  const { plotLowPassFlip, plotTintFrozen, plotTintMirror, plotTintPoint } = deps.injects.state;

  /** sRGB byte to linear, the transfer the samplers apply for a tagged texture. */
  function srgbToLinear(byte: number): number {
    const c = byte / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }
  const SRGB_TO_LINEAR = Float32Array.from({ length: 256 }, (_, i) => srgbToLinear(i));
  const byteTables = new Map<string | null, Float32Array>();
  /** The leaves' bytes to linear, as the tile shader decodes them for this body. */
  function leafToLinear(): Float32Array {
    const body = pyramidView.tileBody;
    let table = byteTables.get(body);
    if (!table) {
      const t = deps.albedoTransferFor(body);
      table = t ? albedoByteTable(t) : SRGB_TO_LINEAR;
      byteTables.set(body, table);
    }
    return table;
  }

  /**
   * The orthoimage's own mean over the ground it covers, raw.
   *
   * The plot shader shades the inherited colour by `ortho / mean`, so that mean
   * has to be THIS image's, over ground rather than over the no-data around it.
   * `siteAlbedo.meanRawFor` is neither: it is the mean of the site index's own
   * low-pass product, over the whole site's rectangle, with every unimaged
   * pixel counted as black. Dividing one image by another image's mean left the
   * drawn albedo 2.1x the leaves' at Hadley - a level step at the SURFACE edge
   * on top of the light one. Raw, with no transfer, because that is how the
   * shader samples it; zero is the bake's no-data and is not ground.
   *
   * Read at up to `ORTHO_MEAN_CAP` on the long side rather than at full size,
   * since a whole-site mosaic is tens of megapixels and the mean of a box
   * filter of it is the mean of it.
   */
  const ORTHO_MEAN_CAP = 1024;
  /** Under this raw value a texel is the bake's no-data and not ground. */
  const NO_DATA_RAW = 2 / 255;
  /**
   * How many rows of the boxed-down image are boxed and summed at a time.
   *
   * The box down is the expensive half and it is one indivisible call, which
   * read 85 ms of a single frame on a 2059 px orthoimage: the canvas is a
   * read-back one, so the resample is software over four megapixels. A band
   * takes exactly the source rows its own destination rows cover, and the sum
   * stays in row order, so the mean is the number the whole image in one blit
   * gives - which is what the pair of runs either side of `no-build-budget`
   * has to agree on.
   */
  const ORTHO_MEAN_BAND_ROWS = 64;
  async function orthoMeanOf(
    tex: THREE.Texture, band: ((name: string) => Promise<void>) | null,
  ): Promise<number | null> {
    const img = tex.image as (ImageBitmap | HTMLImageElement | null | undefined);
    const iw = img?.width ?? 0;
    const ih = img?.height ?? 0;
    if (!img || iw < 1 || ih < 1) return null;
    const k = Math.min(1, ORTHO_MEAN_CAP / Math.max(iw, ih));
    const w = Math.max(1, Math.round(iw * k));
    const h = Math.max(1, Math.round(ih * k));
    const made = readableCanvas2d(w, h);
    if (!made) return null;
    const { ctx } = made;
    try {
      // The decoded bitmap's first CPU touch materializes the whole image
      // regardless of the crop drawn, so a tiny throwaway draw pays that cost
      // in its own held stretch rather than inside band 0's.
      if (band) {
        ctx.drawImage(img, 0, 0, 1, 1, 0, 0, 1, 1);
        ctx.getImageData(0, 0, 1, 1);
        await band('ortho-mean');
      }
      let sum = 0;
      let n = 0;
      // One band of the whole height when there is no budget to keep, which is
      // `drawImage(img, 0, 0, w, h)` again: the control's own frame.
      const step = band ? ORTHO_MEAN_BAND_ROWS : h;
      for (let y = 0; y < h; y += step) {
        const rows = Math.min(step, h - y);
        const sy = Math.round((y * ih) / h);
        const sh = Math.round(((y + rows) * ih) / h) - sy;
        ctx.drawImage(img, 0, sy, iw, sh, 0, y, w, rows);
        const d = ctx.getImageData(0, y, w, rows).data;
        for (let i = 0; i < d.length; i += 4) {
          const v = d[i]! / 255;
          if (v < NO_DATA_RAW) continue;
          sum += v;
          n++;
        }
        if (band) await band('ortho-mean');
      }
      return n > 0 ? Math.max(1e-3, sum / n) : null;
    } catch {
      return null;
    }
  }

  /**
   * The plot's own orthoimage boxed down to the inherited colour's texel.
   *
   * A leaf inside the footprint shades the colour it inherits by
   * `ortho / lowPass(ortho)` cut at `LOWPASS_M` (src/atlas/siteAlbedo.ts). The
   * plot draws the same ground out of the same file, so it divides by the same
   * low-pass rather than by one number for the whole region: a mean is the
   * leaves' colour only where the ground happens to be average, and the ground
   * in frame at Hadley is 2.1x the region's own mean.
   *
   * Built off the texture the plot already samples and left in ITS convention -
   * no flip and no transfer, both matching `uAlbedo` - so the shader's ratio
   * is one image over itself and no frame has to be reasoned about. No flip,
   * not `tex.flipY`: the build calls this before `DtmPlot` sets that flag,
   * and the unpack flip it would carry applies to this canvas but never to the
   * orthoimage's ImageBitmap (src/atlas/imageBitmap.ts).
   */
  function orthoLowPassOf(
    tex: THREE.Texture, widthM: number, heightM: number,
  ): THREE.Texture | null {
    const img = tex.image as (ImageBitmap | HTMLImageElement | null | undefined);
    if (!img || (img.width ?? 0) < 1 || (img.height ?? 0) < 1) return null;
    const w = Math.max(2, Math.round(widthM / LOWPASS_M));
    const h = Math.max(2, Math.round(heightM / LOWPASS_M));
    const made = readableCanvas2d(w, h);
    if (!made) return null;
    try {
      made.ctx.drawImage(img, 0, 0, w, h);
    } catch {
      return null;
    }
    const out = new THREE.CanvasTexture(made.canvas);
    out.name = 'plot-ortho-lowpass';
    out.colorSpace = THREE.NoColorSpace;
    out.wrapS = THREE.ClampToEdgeWrapping;
    out.wrapT = THREE.ClampToEdgeWrapping;
    out.magFilter = THREE.LinearFilter;
    out.minFilter = THREE.LinearFilter;
    out.generateMipmaps = false;
    out.flipY = plotLowPassFlip;
    out.needsUpdate = true;
    return out;
  }

  /**
   * The colour the pyramid draws this ground with, linear rgb.
   *
   * Read off the very texels a leaf over this point inherits: the drawn tile's
   * albedo provider is the resident L6 ancestor and `TileAlbedoRef` says which
   * part of it this tile covers, so averaging that rectangle is the Viking
   * colour of exactly this ground at exactly the scale the plot mesh covers.
   * Averaged in linear light, as the leaf's own sRGB texture is filtered.
   * Null when no leaf deeper than `deeperThan` is drawn here, and `rgb` null
   * when the deepest one is drawn but its colour cannot be read yet; either
   * way the plot keeps its own tint curve until `followPlotGroundColour`
   * hands it one.
   */
  function inheritedGroundColour(
    latRad: number, lonRad: number, deeperThan = -1,
  ): { rgb: [number, number, number] | null; level: number } | null {
    if (pyramidView.tileBody === null) return null;
    for (let level = pyramidView.tileMaxLevel; level > deeperThan; level--) {
      const id = formatTileKey(tileForLatLon(pyramidView.tileBody, latRad, lonRad, level));
      const tile = pyramidView.tileDrawn.get(id);
      if (!tile) continue;
      const unread = { rgb: null, level };
      const ref = pyramidView.tileDraw.get(id)?.albedo ?? null;
      const provider = tile.albedo ? tile : ref?.from ?? null;
      const albedo = provider?.albedo ?? null;
      if (!albedo || typeof ImageBitmap === 'undefined'
        || !(albedo.source instanceof ImageBitmap)) return unread;
      const inherit = tile.albedo ? null : ref;
      const scale = inherit?.scale ?? 1;
      const sw = Math.max(1, Math.round(albedo.width * scale));
      const sh = Math.max(1, Math.round(albedo.height * scale));
      // The rectangle the leaf's own uv samples, row 0 at its v0 edge.
      const [u0, v0] = tileAlbedoUv(0, 0, inherit);
      const sx = Math.round(albedo.width * u0);
      const sy = Math.round(albedo.height * (plotTintMirror ? 1 - v0 - scale : v0));
      const out = plotTintPoint ? 1 : 0;
      const made = readableCanvas2d(out || sw, out || sh);
      if (!made) return unread;
      try {
        // Copied at 1:1 and summed here: a downscale to one pixel is bilinear
        // over a few texels near the middle, not a box over the rectangle.
        made.ctx.drawImage(albedo.source, sx, sy, sw, sh, 0, 0, made.canvas.width, made.canvas.height);
        const d = made.ctx.getImageData(0, 0, made.canvas.width, made.canvas.height).data;
        const toLinear = leafToLinear();
        const rgb: [number, number, number] = [0, 0, 0];
        for (let i = 0; i < d.length; i += 4) {
          rgb[0] += toLinear[d[i]!]!;
          rgb[1] += toLinear[d[i + 1]!]!;
          rgb[2] += toLinear[d[i + 2]!]!;
        }
        const n = d.length / 4;
        return { rgb: [rgb[0] / n, rgb[1] / n, rgb[2] / n], level };
      } catch {
        return unread;
      }
    }
    return null;
  }

  /** Cap on the colour map's long side, cells. */
  const COLOUR_MAP_CAP = 256;
  const COLOUR_MAP_BAND = 1024;
  let cells: {
    field: unknown; w: number; h: number; face: Int8Array; u: Float64Array; v: Float64Array;
  } | null = null;
  function linearToSrgbByte(c: number): number {
    const v = c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(v * 255)));
  }

  /**
   * The colour the tiles draw each part of the plot with, as an sRGB texture in
   * the plot's bbox uv: one anchor colour is right only at the anchor.
   * One cell is one texel of the deepest drawn leaf; `fallback` fills the rest.
   */
  async function inheritedGroundColourMap(
    widthM: number, heightM: number, fallback: readonly [number, number, number],
    band: ((name: string) => Promise<void>) | null,
  ): Promise<THREE.DataTexture | null> {
    const body = pyramidView.tileBody;
    if (body === null || !deps.live.plotField || typeof ImageBitmap === 'undefined') return null;
    const toLinear = leafToLinear();
    const k = Math.min(1, COLOUR_MAP_CAP / Math.max(widthM / LOWPASS_M, heightM / LOWPASS_M));
    const w = Math.max(2, Math.round((widthM / LOWPASS_M) * k));
    const h = Math.max(2, Math.round((heightM / LOWPASS_M) * k));
    // Where each cell stands on the cube, which only a new plot changes.
    const field = deps.live.plotField;
    if (!cells || cells.field !== field || cells.w !== w || cells.h !== h) {
      const face = new Int8Array(w * h);
      const u = new Float64Array(w * h);
      const v = new Float64Array(w * h);
      for (let j = 0; j < h; j++) {
        for (let i = 0; i < w; i++) {
          const ll = deps.plotGeometry.latLonAt(((i + 0.5) / w - 0.5) * widthM, ((j + 0.5) / h - 0.5) * heightM);
          const fu = faceUvForDirection(directionForLatLon((ll.lat * Math.PI) / 180, (ll.lon * Math.PI) / 180));
          face[j * w + i] = fu.face;
          u[j * w + i] = fu.u;
          v[j * w + i] = fu.v;
        }
        if (band) await band('ground-colour-map');
      }
      cells = { field, w, h, face, u, v };
    }
    const grid = cells;
    // The drawn leaves indexed once by level and a number: a string key per
    // level per cell was most of a 58 ms build at Jezero.
    const drawnAt = new Map<number, Map<number, string>>();
    for (const [id, tile] of pyramidView.tileDrawn) {
      const { key } = tile;
      if (key.body !== body) continue;
      const n = tilesAcross(key.level);
      let row = drawnAt.get(key.level);
      if (!row) drawnAt.set(key.level, row = new Map());
      row.set((key.face * n + key.x) * n + key.y, id);
    }
    const levels = [...drawnAt.keys()].sort((p, q) => q - p);
    // Which bitmap each cell samples and where, then only the rectangle of
    // each bitmap the cells reach is read back.
    type Source = { bitmap: ImageBitmap; x0: number; y0: number; x1: number; y1: number; img: ImageData | null };
    const sources: Source[] = [];
    const sourceIndex = new Map<ImageBitmap, number>();
    const byLeaf = new Map<string, { at: number; ref: TileAlbedoRef | null } | null>();
    const cellSrc = new Int32Array(w * h).fill(-1);
    const cellX = new Float32Array(w * h);
    const cellY = new Float32Array(w * h);
    for (let c = 0; c < w * h; c++) {
      if (band && c % COLOUR_MAP_BAND === COLOUR_MAP_BAND - 1) await band('ground-colour-map');
      const fc = grid.face[c]!;
      const fu = grid.u[c]!;
      const fv = grid.v[c]!;
      for (const level of levels) {
        const n = tilesAcross(level);
        const x = Math.min(n - 1, Math.max(0, Math.floor(fu * n)));
        const y = Math.min(n - 1, Math.max(0, Math.floor(fv * n)));
        const id = drawnAt.get(level)?.get((fc * n + x) * n + y);
        if (id === undefined) continue;
        let leaf = byLeaf.get(id);
        if (leaf === undefined) {
          const tile = pyramidView.tileDrawn.get(id);
          const ref = tile && !tile.albedo ? pyramidView.tileDraw.get(id)?.albedo ?? null : null;
          const bitmap = (tile?.albedo ? tile : ref?.from ?? null)?.albedo?.source;
          leaf = null;
          if (bitmap instanceof ImageBitmap && bitmap.width > 0 && bitmap.height > 0) {
            let at = sourceIndex.get(bitmap);
            if (at === undefined) {
              at = sources.length;
              sourceIndex.set(bitmap, at);
              sources.push({ bitmap, x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity, img: null });
            }
            leaf = { at, ref };
          }
          byLeaf.set(id, leaf);
        }
        if (!leaf) break;
        const src = sources[leaf.at]!;
        const [tu, tv] = tileAlbedoUv(fu * n - x, fv * n - y, leaf.ref);
        const bw = src.bitmap.width;
        const bh = src.bitmap.height;
        const px = Math.min(bw - 1, Math.max(0, tu * bw - 0.5));
        const py = Math.min(bh - 1, Math.max(0, (plotTintMirror ? 1 - tv : tv) * bh - 0.5));
        cellSrc[c] = leaf.at;
        cellX[c] = px;
        cellY[c] = py;
        src.x0 = Math.min(src.x0, Math.floor(px));
        src.y0 = Math.min(src.y0, Math.floor(py));
        src.x1 = Math.max(src.x1, Math.min(bw - 1, Math.floor(px) + 1));
        src.y1 = Math.max(src.y1, Math.min(bh - 1, Math.floor(py) + 1));
        break;
      }
    }
    for (const src of sources) {
      if (band) await band('ground-colour-map');
      const sw = src.x1 - src.x0 + 1;
      const sh = src.y1 - src.y0 + 1;
      const made = readableCanvas2d(sw, sh);
      if (!made) continue;
      try {
        made.ctx.drawImage(src.bitmap, src.x0, src.y0, sw, sh, 0, 0, sw, sh);
        src.img = made.ctx.getImageData(0, 0, sw, sh);
      } catch {
        src.img = null;
      }
    }
    const data = new Uint8Array(w * h * 4);
    const rgb: [number, number, number] = [0, 0, 0];
    let read = 0;
    for (let c = 0; c < w * h; c++) {
      if (band && c % COLOUR_MAP_BAND === COLOUR_MAP_BAND - 1) await band('ground-colour-map');
      const src = cellSrc[c]! >= 0 ? sources[cellSrc[c]!]! : null;
      const img = src?.img ?? null;
      if (src && img) {
        const px = cellX[c]! - src.x0;
        const py = cellY[c]! - src.y0;
        const x0 = Math.floor(px);
        const y0 = Math.floor(py);
        const x1 = Math.min(img.width - 1, x0 + 1);
        const y1 = Math.min(img.height - 1, y0 + 1);
        const fx = px - x0;
        const fy = py - y0;
        const d = img.data;
        const r0 = (y0 * img.width + x0) * 4;
        const r1 = (y0 * img.width + x1) * 4;
        const r2 = (y1 * img.width + x0) * 4;
        const r3 = (y1 * img.width + x1) * 4;
        for (let k = 0; k < 3; k++) {
          rgb[k] = (toLinear[d[r0 + k]!]! * (1 - fx) + toLinear[d[r1 + k]!]! * fx) * (1 - fy)
            + (toLinear[d[r2 + k]!]! * (1 - fx) + toLinear[d[r3 + k]!]! * fx) * fy;
        }
        read++;
      } else {
        rgb[0] = fallback[0]; rgb[1] = fallback[1]; rgb[2] = fallback[2];
      }
      const o = c * 4;
      data[o] = linearToSrgbByte(rgb[0]);
      data[o + 1] = linearToSrgbByte(rgb[1]);
      data[o + 2] = linearToSrgbByte(rgb[2]);
      data[o + 3] = 255;
    }
    if (read === 0) return null;
    const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
    tex.name = 'plot-leaf-colour';
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.flipY = false;
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * How long an owed read waits before retrying, wall time.
   *
   * An owed colour can only newly resolve on a pyramid traversal
   * (`pyramidView.ts`'s own `TILE_REQUEST_S`, 100 ms): a kept tile's albedo
   * provider changing is a retarget (`pyramidView.ts:1115-1143`), and a
   * retarget alone does not set `tileDrawChanged`, which only reports a mount
   * or unmount (`pyramidView.ts:1099`). So nothing this read is owed on can
   * change faster than one traversal either way, and retrying more often than
   * that only repeats the same 1x1 canvas readback for the same answer.
   */
  const PLOT_GROUND_OWED_RETRY_S = 0.1;

  /** A colour map rebuilt off the frame loop gives the frame back past this, ms. */
  const COLOUR_MAP_SLICE_MS = 4;
  let colourMapJob = 0;
  function frameBand(): (name: string) => Promise<void> {
    let held = performance.now();
    return async () => {
      if (performance.now() - held < COLOUR_MAP_SLICE_MS) return;
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      held = performance.now();
    };
  }

  /**
   * DEV-only: bumped once per owed retry `followPlotGroundColour` actually
   * takes, reset at mount. `plot-colour-check.mjs` samples
   * `window.__plotGroundReadbacks` to show the throttle above working;
   * `?plotgroundretrylegacy=1` turns it back off to read the pre-fix rate.
   */
  if (import.meta.env.DEV) window.__plotGroundReadbacks = 0;
  const plotGroundRetryLegacy = TEST_SWITCHES && typeof location !== 'undefined'
    && new URLSearchParams(location.search).get('plotgroundretrylegacy') === '1';

  /**
   * The leaves' colour handed to the plot after its build, and again each
   * time a deeper leaf is drawn under its anchor.
   *
   * Read only at the build, the colour was whatever the pyramid had drawn
   * there by then: a build that beat the leaves kept the albedo ramp's
   * fallback, or a coarse leaf's average, for the whole visit, so the same
   * stand drew one of several grounds depending on how fast the tiles came.
   * Only ever deeper, so a leaf coarsening as the eye walks off does not
   * repaint the ground under it.
   */
  function followPlotGroundColour(dt: number): void {
    const g = deps.plotBuild.state.plotGround;
    if (!g) return;
    if (g.plot !== deps.live.plot && g.plot !== deps.plotBuild.state.plotBuilding) { deps.plotBuild.state.plotGround = null; return; }
    if (plotTintFrozen) return;
    if (!pyramidView.tileDrawChanged) {
      if (!g.owed) return;
      if (!plotGroundRetryLegacy) {
        g.owedWaitS += dt;
        if (g.owedWaitS < PLOT_GROUND_OWED_RETRY_S) return;
      }
      if (import.meta.env.DEV) window.__plotGroundReadbacks = (window.__plotGroundReadbacks ?? 0) + 1;
    }
    g.owedWaitS = 0;
    const got = inheritedGroundColour(g.latRad, g.lonRad, g.level);
    g.owed = got !== null && got.rgb === null;
    if (!got?.rgb) return;
    const lowPass = g.ortho && g.orthoMean !== null && !g.hasLowPass
      ? orthoLowPassOf(g.ortho, g.widthM, g.heightM) : null;
    if (lowPass) g.hasLowPass = true;
    g.plot.setInheritedColour(got.rgb, got.level, g.ortho ? g.orthoMean : null, lowPass);
    const plot = g.plot;
    const job = ++colourMapJob;
    void inheritedGroundColourMap(g.widthM, g.heightM, got.rgb, frameBand()).then((map) => {
      const current = plot === deps.live.plot || plot === deps.plotBuild.state.plotBuilding;
      if (job !== colourMapJob || !current) { map?.dispose(); return; }
      plot.setColourMap(map);
      deps.live.needsRender = true;
    });
    g.level = got.level;
    deps.live.needsRender = true;
  }

  return {
    orthoMeanOf, orthoLowPassOf, inheritedGroundColour, inheritedGroundColourMap,
    followPlotGroundColour, srgbToLinear,
  };
}
